import { FC, ReactNode } from 'react';

import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import styles from './PunchupAnalyticsSection.module.css';

export interface AnalyticsSectionRow {
	/**
	 * Chart cards or metric tiles to display in this row.
	 * Row count drives column count: 1 item spans full width, 2+ are side-by-side.
	 */
	items: ReactNode[];
	/**
	 * Grid layout for this row.
	 * 'charts' (default) — auto-fit ≥280px columns, for chart cards.
	 * 'metrics' — auto-fill ≥180px columns, for stat tiles.
	 */
	variant?: 'charts' | 'metrics';
}

export interface PunchupAnalyticsSectionProps {
	/**
	 * Section heading — displayed as h3 to sit below the page h2.
	 * Matches the title shown in the Punchup analytics sub-page sections
	 * (e.g. "Ticket Sales", "Revenue", "Ticket Inventory").
	 */
	title: string;
	/**
	 * One-line description shown below the title in muted secondary text.
	 * Use to give context for what the section measures
	 * (e.g. "Track ticket sales and revenue over time").
	 */
	description?: string;
	/**
	 * Optional icon rendered to the left of the title.
	 * Accepts any ReactNode — typically a Lucide icon or equivalent SVG element
	 * sized to 20×20px (the iconSlot enforces that box).
	 */
	icon?: ReactNode;
	/**
	 * Structured row definitions. Each row renders its items in a responsive grid.
	 * When provided, takes priority over children.
	 * Use this to define layout at the section level without a separate PunchupAnalyticsGrid.
	 */
	rows?: AnalyticsSectionRow[];
	/** Free-form children — used when rows is not provided. */
	children?: ReactNode;
	/** When true, replaces content with a centred loading spinner — no layout shift. */
	isLoading?: boolean;
}

export const PunchupAnalyticsSection: FC<PunchupAnalyticsSectionProps> = ({
	title,
	description,
	icon,
	rows,
	children,
	isLoading,
}) => (
	<section className={styles.root}>
		<div className={styles.header}>
			<div className={styles.titleRow}>
				{icon && <span className={styles.iconSlot}>{icon}</span>}
				<h3 className={styles.title}>{title}</h3>
			</div>
			{description && <p className={styles.description}>{description}</p>}
		</div>
		{isLoading ? (
			<div className={styles.loadingShell}>
				<PunchupSpinner size={32} />
			</div>
		) : rows ? (
			rows.map((row, i) => (
				<div key={i} className={row.variant === 'metrics' ? styles.metricsRow : styles.chartsRow}>
					{row.items}
				</div>
			))
		) : (
			children
		)}
	</section>
);
